import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Select, MenuItem, FormControl, InputLabel,
  Alert, Tooltip, Chip, InputAdornment, CircularProgress,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';
import { downloadUserImportTemplate, parseUserImportFile, ImportUserRow } from '../utils/userImport';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  created_at?: string;
  last_qq_comment_time?: string;
}

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role: 'user' });
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportUserRow[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; created: number; skipped: number; errors: { row: number; email: string; message: string }[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUsers = useCallback(async () => {
    const response = await axios.get('/api/users', {
      params: { page: page + 1, limit: rowsPerPage, search: search || undefined },
    });
    setUsers(response.data.users || []);
    setTotal(response.data.total || 0);
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchUsers().catch(console.error); }, [fetchUsers]);

  const handleOpen = (user?: User) => {
    setError('');
    if (user) {
      setEditingUser(user);
      setFormData({ username: user.username, email: user.email, password: '', role: user.role });
    } else {
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'user' });
    }
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const payload: Record<string, string> = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
      };
      if (formData.password) payload.password = formData.password;
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, payload);
      } else {
        if (!formData.password) { setError('新用户必须填写密码'); return; }
        await axios.post('/api/users', payload);
      }
      setOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '保存用户失败');
    }
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '-';
    return new Date(timeStr).toLocaleString('zh-CN', { hour12: false });
  };

  const isOnline = (timeStr?: string) => {
    if (!timeStr) return false;
    return Date.now() - new Date(timeStr).getTime() <= 15 * 60 * 1000;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setImportResult(null);
    try {
      const rows = await parseUserImportFile(file);
      setImportRows(rows);
      setImportOpen(true);
    } catch (err: any) {
      setError(err.message || '解析 Excel 失败');
    }
  };

  const handleImport = async () => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const response = await axios.post('/api/users/import', { users: importRows });
      setImportResult(response.data);
      if (response.data.created > 0) fetchUsers();
    } catch (err: any) {
      setImportResult(null);
      setError(err.response?.data?.message || '批量导入失败');
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportDialog = () => {
    setImportOpen(false);
    setImportRows([]);
    setImportResult(null);
  };

  return (
    <SidebarLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">用户管理</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" placeholder="搜索用户名" value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (setSearch(searchInput), setPage(0))}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearch(searchInput); setPage(0); }}><SearchIcon fontSize="small" /></IconButton></InputAdornment> }} />
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadUserImportTemplate}>下载示例</Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>批量导入</Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
            <Button variant="contained" onClick={() => handleOpen()}>添加用户</Button>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>用户名</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>QQ音乐活跃</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell><Chip label={user.role === 'admin' ? '管理员' : '用户'} size="small" /></TableCell>
                  <TableCell>{formatTime(user.created_at)}</TableCell>
                  <TableCell>
                    <Tooltip title={formatTime(user.last_qq_comment_time)}>
                      <Typography variant="body2" color={isOnline(user.last_qq_comment_time) ? 'success.main' : 'text.secondary'}>
                        {isOnline(user.last_qq_comment_time) ? '在线' : '离线'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpen(user)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(user.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingUser ? '编辑用户' : '添加用户'}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="用户名" value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} margin="normal" required />
            <TextField fullWidth label="邮箱" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} margin="normal" required />
            <TextField fullWidth label={editingUser ? '新密码（可选）' : '密码'} type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} margin="normal" />
            <FormControl fullWidth margin="normal">
              <InputLabel>角色</InputLabel>
              <Select value={formData.role} label="角色" onChange={e => setFormData({ ...formData, role: e.target.value })}>
                <MenuItem value="user">普通用户</MenuItem>
                <MenuItem value="admin">管理员</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>取消</Button>
            <Button onClick={handleSubmit} variant="contained">保存</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={importOpen} onClose={closeImportDialog} maxWidth="md" fullWidth>
          <DialogTitle>批量导入用户</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              导入用户默认角色为普通用户。表头需包含：用户名、邮箱、密码。
            </Typography>
            {importResult ? (
              <Box>
                <Alert severity={importResult.created > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  共 {importResult.total} 条，成功 {importResult.created} 条，跳过 {importResult.skipped} 条
                </Alert>
                {importResult.errors.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>行号</TableCell>
                          <TableCell>邮箱</TableCell>
                          <TableCell>原因</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResult.errors.map((err, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{err.row}</TableCell>
                            <TableCell>{err.email || '-'}</TableCell>
                            <TableCell>{err.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>用户名</TableCell>
                      <TableCell>邮箱</TableCell>
                      <TableCell>密码</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.username}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>••••••••</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeImportDialog}>{importResult ? '关闭' : '取消'}</Button>
            {!importResult && (
              <Button onClick={handleImport} variant="contained" disabled={importLoading || importRows.length === 0}>
                {importLoading ? <CircularProgress size={20} /> : `确认导入（${importRows.length} 条）`}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );

  async function handleDelete(id: number) {
    if (!window.confirm('确定删除？')) return;
    await axios.delete(`/api/users/${id}`);
    fetchUsers();
  }
};

export default UserManagement;
